<script setup>
import { Link, usePage } from "@inertiajs/vue3";
import Button from "@/Components/ui/button/Button.vue";
import ButtonArrow from "@/Components/svg/ButtonArrow.vue";
import { str_contains, useInnerHeight } from "@/lib/utils.js";
import { computed } from "vue";

const props = defineProps({
  href: {
    type: String,
  },
  target: {
    type: String,
    default: "_self",
  },
  showArrow: {
    type: Boolean,
    default: true,
  },
  link: {
    type: [Object, Boolean],
    default: false,
    required: false,
  },
  arrowColor: {
    type: String,
    default: "text-brand-blue",
  },
  isHeaderItem: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "pageLinkClicked",
]);

const { url } = usePage();

const href = computed(() => {
  if (props.link === false) {
    return props.href;
  }
  return props.link.href;
});

const target = computed(() => {
  if (props.link === false) {
    return props.target;
  }
  return props.link.target;
});

const isExternal = computed(() => {
  if (props.link === false) {
    return false;
  }
  return props.link.external;
});

const isAnchor = computed(() => {
  if (props.link === false) {
    return props.href.startsWith("#");
  }

  return props.link?.anchor;
});

const isActive = computed(() => {
  if (props.isHeaderItem === false || url === "/") {
    return false;
  }
  return str_contains(href.value, url);
});

const scrollToSection = (e, isAnchor) => {
  if (isAnchor) {
    e.preventDefault();

    const target = document.querySelector(href.value);

    if (!target) {
      console.error(`Element with id ${props.href} not found`);
      return;
    }

    const pageHeader = useInnerHeight(document.querySelector("#page-header"));

    if (typeof window === "undefined") {
      return;
    }
    window.scrollTo({
      top: target.getBoundingClientRect().top - pageHeader,
      behavior: "smooth",
    });
  }
};
</script>

<template>
  <Button
    @click="(e) => emit('pageLinkClicked', true)"
    class=""
    variant="link"
    size="link"
    as-child
  >
    <a
      v-if="isAnchor || isExternal"
      :href="href"
      :target="target"
      @click="(e) => scrollToSection(e, isAnchor)"
      class="anchor-or-external-link"
    >
      <slot />
      <ButtonArrow v-if="props.showArrow" class="w-full max-w-3" />
    </a>
    <Link
      class="internal-link"
      :class="isActive ? '!italic !text-brand-blue' : ''"
      v-else-if="href && target"
      :href="href"
      :target="target"
    >
      <slot />
      <ButtonArrow
        v-if="props.showArrow"
        :class="arrowColor"
        class="w-full fill-current max-w-3.5"
      />
    </Link>
  </Button>
</template>

<style scoped></style>
